import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DocumentVerificationStep = ({ formData, updateFormData, onBack, onSubmit, isSubmitting }) => {
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.driverLicense) {
      newErrors.driverLicense = 'Driver\'s license is required';
    }

    if (!formData?.insuranceDocument) {
      newErrors.insuranceDocument = 'Auto insurance verification is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleFileChange = (e, fieldName) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file?.size > maxSize) {
        setErrors(prev => ({ ...prev, [fieldName]: 'File size must be less than 5MB' }));
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes?.includes(file?.type)) {
        setErrors(prev => ({ ...prev, [fieldName]: 'Only JPG, PNG, WebP, or PDF files are allowed' }));
        return;
      }

      updateFormData({ [fieldName]: file });
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleRemoveFile = (fieldName) => {
    updateFormData({ [fieldName]: null });
    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit();
    }
  };

  const renderFileUpload = (fieldName, label, description, icon) => {
    const file = formData?.[fieldName];
    const error = errors?.[fieldName];

    return (
      <div>
        <label className="font-body text-sm font-medium text-foreground mb-2 block">
          {label} <span className="text-destructive">*</span>
        </label>
        <p className="font-caption text-xs text-muted-foreground mb-3">{description}</p>

        {!file ? (
          <label className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-smooth ${
            error ? 'border-error bg-error/5' : 'border-border hover:border-primary bg-muted/30'
          }`}>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => handleFileChange(e, fieldName)}
              className="hidden"
            />
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name={icon} size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <p className="font-body text-sm text-foreground mb-1">Click to upload or drag and drop</p>
                <p className="font-caption text-xs text-muted-foreground">JPG, PNG, WebP, or PDF (max 5MB)</p>
              </div>
            </div>
          </label>
        ) : (
          <div className="border-2 border-primary bg-primary/5 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="FileCheck" size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm font-medium text-foreground truncate">{file?.name}</p>
                <p className="font-caption text-xs text-muted-foreground">
                  {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveFile(fieldName)}
                className="w-8 h-8 bg-error text-error-foreground rounded-full flex items-center justify-center hover:scale-110 transition-smooth flex-shrink-0"
                aria-label="Remove file"
              >
                <Icon name="X" size={16} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        )}

        {error && (
          <p className="mt-2 text-sm text-destructive flex items-center space-x-1">
            <Icon name="AlertCircle" size={14} strokeWidth={2} />
            <span>{error}</span>
          </p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">Document Verification</h2>
        <p className="font-body text-sm text-muted-foreground">Upload required documents to verify your identity</p>
      </div>

      <div className="p-4 bg-muted rounded-md flex items-start space-x-3">
        <Icon name="Shield" size={20} color="var(--color-primary)" strokeWidth={2} />
        <div className="flex-1">
          <p className="font-body text-sm font-semibold text-foreground mb-1">Why do we need these documents?</p>
          <p className="font-caption text-xs text-muted-foreground">
            We require valid driver's license and auto insurance verification to ensure the safety and security of all users on our platform. Your documents are encrypted and stored securely.
          </p>
        </div>
      </div>

      {renderFileUpload(
        'driverLicense',
        'Driver\'s License',
        'Upload a clear photo or scan of your valid driver\'s license',
        'CreditCard'
      )}

      {renderFileUpload(
        'insuranceDocument',
        'Auto Insurance Verification',
        'Upload proof of current auto insurance coverage',
        'FileText'
      )}

      <div className="p-4 bg-primary/5 border border-primary rounded-md flex items-start space-x-3">
        <Icon name="Info" size={20} color="var(--color-primary)" strokeWidth={2} />
        <div className="flex-1">
          <p className="font-body text-sm text-foreground">
            <span className="font-semibold">Document Tips:</span>
          </p>
          <ul className="font-caption text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
            <li>Ensure all text is clearly visible and readable</li>
            <li>Documents must be current and not expired</li>
            <li>Photos should be well-lit without glare or shadows</li>
            <li>Verification typically takes 24-48 hours</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          iconName="ArrowLeft"
          className="flex-1"
        >
          Back
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="Check"
          className="flex-1"
        >
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </Button>
      </div>
    </form>
  );
};

export default DocumentVerificationStep;